import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { FeatureRequestApiService } from '@shure/cloud/shared/feature-request/data-access';
import { AnalyticsEvents, AnalyticsService } from '@shure/cloud/shared/utils/analytics';
import {
	PrismButtonComponent,
	PrismDialogComponent,
	PrismDialogRef,
	PrismInputComponent,
	PrismSnackbarService,
	PrismTextareaComponent,
	TextValueAccessor
} from '@shure/prism-angular-components';

import { SubmitFeatureRequest } from '../../models/feature-request.model';

@Component({
	selector: 'sh-feature-request-dialog',
	standalone: true,
	imports: [
		CommonModule,
		FlexLayoutModule,
		FormsModule,
		ReactiveFormsModule,
		TranslocoModule,
		TextValueAccessor,
		PrismInputComponent,
		PrismButtonComponent,
		PrismDialogComponent,
		PrismTextareaComponent
	],
	templateUrl: './feature-request-dialog.component.html',
	styleUrls: ['./feature-request-dialog.component.scss']
})
export class FeatureRequestDialogComponent implements OnInit, OnDestroy {
	public showSnackbar = signal(false);
	public formGroup: FormGroup = new FormGroup({});
	public formData = [
		{
			name: 'title',
			validators: []
		},
		{
			name: 'description',
			validators: [Validators.required]
		}
	];
	private readonly destroy$ = new Subject<void>();
	public isFormChanged = true;

	constructor(
		private formBuilder: FormBuilder,
		private dialogRef: PrismDialogRef<FeatureRequestDialogComponent>,
		private analyticsService: AnalyticsService,
		public translocoService: TranslocoService,
		public featureRequestService: FeatureRequestApiService,
		private snackBarService: PrismSnackbarService
	) {}

	public ngOnInit(): void {
		this.formGroup = this.formBuilder.group({});
		this.formData?.forEach((formControlDetail) => {
			this.formGroup.addControl(formControlDetail.name, new FormControl('', formControlDetail.validators));
		});
	}

	public ngOnDestroy(): void {
		this.destroy$.complete();
	}

	public doAction(): void {
		if (this.formGroup.valid) {
			/* eslint-disable @typescript-eslint/naming-convention */
			this.analyticsService.analyticDataCollector(AnalyticsEvents.HelpRequested, { request_type: 'feature' });
		}

		if (this.formGroup.valid) {
			const requestData = this.formGroup.value;
			this.analyticsService.analyticDataCollector(AnalyticsEvents.HelpRequested, { request_type: 'feature' });
			this.submitFeatureRequest(requestData); // Call API
		}
	}

	public closeDialog(): void {
		this.dialogRef.close();
	}

	/**
	 *	This method is called to submit the feature request using the response
	 * 	@param featureRequestData - object of type FeatureRequestDto
	 */
	public submitFeatureRequest(featureRequestData: SubmitFeatureRequest): void {
		this.closeDialog();
		this.featureRequestService
			.submitFeatureRequest$Response({ body: featureRequestData })
			?.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (featureRequestResponse) => {
					const featureRequestResponseBody = featureRequestResponse?.body;
					this.snackBarService.open({
						message: this.translocoService.translate(
							'cloud.shared.header.' + featureRequestResponseBody?.body?.messageKey
						),
						actionText: this.translocoService.translate('common.buttons.ok')
					});
				},
				error: (error) => {
					const errorResponse = error.error;
					this.snackBarService.open({
						message: this.translocoService.translate(
							errorResponse.i18nKey
								? 'cloud.shared.error-labels.' + errorResponse.i18nKey
								: errorResponse.message
						),
						actionText: this.translocoService.translate('common.buttons.ok')
					});
				}
			});
	}
}
